import { Suspense } from 'react';
import { lazyPage } from '@/lib/chunkReload';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
// Subscriptions removed; manage in Donations/Stripe portal
import { Toaster } from 'sonner';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useTabSync } from '@/hooks/useTabSync';
import ScrollToTop from '@/components/ScrollToTop';
import ConsentBanner from '@/components/ConsentBanner';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
// NOTE: everything imported directly below (through NotFound's import) is one of
// vite.config.ts's PRERENDER_ROUTES. The puppeteer prerenderer snapshots each
// route shortly after mount (main.tsx's double-rAF 'render-event', independent of
// whether any lazy chunk has resolved) — lazy-loading any of these would make the
// prerendered HTML capture <LoadingSpinner/> instead of real content. Only
// authenticated/dashboard/admin/auth routes (never prerendered) are lazy below.
import HowItWorks from './pages/HowItWorks';
import BestPractices from './pages/BestPractices';
import About from './pages/About';
import ParentPlusCalculator from './pages/ParentPlusCalculator';
import SignUpCreator from './pages/SignUpCreator';
import Resources from './pages/Resources';
import GivingWithPurpose from './pages/resources/GivingWithPurpose';
import SmartGivingGuide from './pages/resources/SmartGivingGuide';
import StrategicStewardship from './pages/resources/StrategicStewardship';
import SupportingLocalChurch from './pages/resources/SupportingLocalChurch';
import SnapRaiseAlternatives from './pages/resources/SnapRaiseAlternatives';
import CostOfYouthSports2026 from './pages/resources/CostOfYouthSports2026';
import HowToFundraiseForASeason from './pages/resources/HowToFundraiseForASeason';
import FundraisingLetterTemplate from './pages/resources/FundraisingLetterTemplate';
import ThankYouMessageTemplates from './pages/resources/ThankYouMessageTemplates';
import HowToSetAFundraisingGoal from './pages/resources/HowToSetAFundraisingGoal';
import SponsorshipLetterTemplate from './pages/resources/SponsorshipLetterTemplate';
import DriveSportsAndTeamFundraisers from './pages/resources/DriveSportsAndTeamFundraisers';
import SpotfundVsSportFund from './pages/resources/SpotfundVsSportFund';
import RecurringVsOneTimeDonations from './pages/resources/RecurringVsOneTimeDonations';
import YouthSportsGrants from './pages/resources/hubs/YouthSportsGrants';
import NationalChampionshipsCalendar from './pages/resources/hubs/NationalChampionshipsCalendar';
import FundraisingGuidesIndex from './pages/resources/hubs/FundraisingGuidesIndex';
import SportFundraisingGuide from './pages/resources/hubs/SportFundraisingGuide';
import PodcastsAndBooks from './pages/resources/hubs/PodcastsAndBooks';
import ArticleChrome from './components/content/ArticleChrome';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AcceptableUse from './pages/AcceptableUse';

const SignUp = lazyPage(() => import('./pages/SignUp'));
const SignIn = lazyPage(() => import('./pages/SignIn'));
const MissionPage = lazyPage(() => import('./pages/MissionPage'));
const DemoMissionPage = lazyPage(() => import('./pages/DemoMissionPage'));
const AuthEmailHandler = lazyPage(() => import('./pages/AuthEmailHandler'));
const PasswordReset = lazyPage(() => import('./pages/PasswordReset'));
const EmailSignIn = lazyPage(() => import('./pages/EmailSignIn'));
const UserSettings = lazyPage(() => import('./pages/dashboard/UserSettings'));
const Donations = lazyPage(() => import('./pages/dashboard/Donations'));
const DashboardHome = lazyPage(() => import('./pages/dashboard/DashboardHome'));
const MemberDashboard = lazyPage(() => import('./components/dashboard/MemberDashboard'));
const DashboardLayout = lazyPage(() => import('./components/dashboard/DashboardLayout'));
const MyPage = lazyPage(() => import('./pages/dashboard/MyPage'));
const Membership = lazyPage(() => import('./pages/dashboard/Membership'));
const DonationsPage = lazyPage(() => import('@/components/donations/DonationsPage'));
const AudienceUsersPage = lazyPage(() => import('@/components/audience/AudienceUsersPage'));
const AdminDashboard = lazyPage(() => import('@/pages/admin/Dashboard'));
const Users = lazyPage(() => import('@/pages/admin/Users'));
const Compliance = lazyPage(() => import('@/pages/admin/Compliance'));
// Content pages (creator-only, auth-gated)
const CreatorContentList = lazyPage(() => import('./pages/creator/content/index'));
const AddContentPage = lazyPage(() => import('./pages/creator/content/add'));
const ContentDetail = lazyPage(() => import('./pages/creator/content/[contentId]'));
const EditContent = lazyPage(() => import('./pages/creator/content/edit'));
const PaymentSuccess = lazyPage(() => import('./pages/payment/PaymentSuccess'));
const PaymentCancel = lazyPage(() => import('./pages/payment/PaymentCancel'));
// Not prerendered (SPA-fallback only, served to crawlers as noindex via TSF-34)
const NotFound = lazyPage(() => import('./pages/NotFound'));

export default function App() {
  // Navigating away from a crashed page clears the error screen.
  const { pathname } = useLocation();
  usePageTracking();
  useTabSync();
  return (
    <>
      <ScrollToTop />
      <Toaster />
      <ConsentBanner />
      <AppErrorBoundary key={pathname}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/best-practices" element={<BestPractices />} />
          <Route path="/faqs" element={<BestPractices />} />
          <Route path="/about" element={<About />} />
          <Route path="/parent-plus" element={<ParentPlusCalculator />} />
          <Route path="/pricing" element={<Navigate to="/faqs" replace />} />
          <Route path="/resources" element={<Resources />} />
          {/* Long-form guides: bare article markup, so they get the site header/footer here. */}
          <Route element={<ArticleChrome />}>
            <Route path="/resources/funding-your-education" element={<GivingWithPurpose />} />
            <Route path="/resources/smart-fundraising-guide" element={<SmartGivingGuide />} />
            <Route path="/resources/engaging-your-supporters" element={<StrategicStewardship />} />
            <Route path="/resources/supporting-an-athlete" element={<SupportingLocalChurch />} />
            <Route path="/resources/snap-raise-alternatives" element={<SnapRaiseAlternatives />} />
            <Route path="/resources/cost-of-youth-sports-2026" element={<CostOfYouthSports2026 />} />
            <Route path="/resources/how-to-fundraise-for-a-sports-season" element={<HowToFundraiseForASeason />} />
            <Route path="/resources/fundraising-letter-template" element={<FundraisingLetterTemplate />} />
            <Route path="/resources/thank-you-message-templates" element={<ThankYouMessageTemplates />} />
            <Route path="/resources/how-to-set-a-fundraising-goal" element={<HowToSetAFundraisingGoal />} />
            <Route path="/resources/sponsorship-letter-template" element={<SponsorshipLetterTemplate />} />
            <Route path="/resources/drive-sports-and-team-fundraisers" element={<DriveSportsAndTeamFundraisers />} />
            <Route path="/resources/spotfund-vs-sport-fund" element={<SpotfundVsSportFund />} />
            <Route path="/resources/recurring-vs-one-time-donations" element={<RecurringVsOneTimeDonations />} />
          </Route>
          {/* Content hubs (docs/06-Go-to-Market.md, "Content strategy") — each renders its own PageShell. */}
          <Route path="/resources/youth-sports-grants" element={<YouthSportsGrants />} />
          <Route path="/resources/national-championships-calendar" element={<NationalChampionshipsCalendar />} />
          <Route path="/resources/fundraising-guides" element={<FundraisingGuidesIndex />} />
          <Route path="/resources/fundraising-guides/:sportSlug" element={<SportFundraisingGuide />} />
          <Route path="/resources/podcasts-and-books-for-sports-parents" element={<PodcastsAndBooks />} />
          {/* Legacy routes for old resource URLs */}
          <Route path="/resources/giving-with-purpose" element={<Navigate to="/resources/funding-your-education" replace />} />
          <Route path="/resources/smart-giving-guide" element={<Navigate to="/resources/smart-fundraising-guide" replace />} />
          <Route path="/resources/strategic-stewardship" element={<Navigate to="/resources/engaging-your-supporters" replace />} />
          <Route path="/resources/supporting-your-local-church" element={<Navigate to="/resources/supporting-an-athlete" replace />} />
          <Route path="/resources/supporting-a-student" element={<Navigate to="/resources/supporting-an-athlete" replace />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          {/* German-language legal-notice paths a §5 DDG imprint bot probes daily
              (TSF play: point-impressum-datenschutz-and-kontakt-at-the-english).
              Sport Fund is operated by BackerBase LLC, a Wyoming LLC, not a
              German entity, so there is no statutory Impressum to author -- these
              just serve the existing English legal pages instead of 404ing. */}
          <Route path="/impressum" element={<Terms />} />
          <Route path="/datenschutz" element={<Privacy />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/signup-creator" element={<SignUpCreator />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/email-signin" element={<EmailSignIn />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="/fund/demo" element={<DemoMissionPage />} />
          <Route path="/fund/:creatorId" element={<MissionPage />} />
          {/* Legacy route - redirect /mission/ to /fund/ */}
          <Route path="/mission/:creatorId" element={<MissionPage />} />
          <Route path="/auth/email-handler" element={<AuthEmailHandler />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          {/* Protected Member Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute userType="member">
                <MemberDashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="settings" element={<UserSettings />} />
            <Route path="donations" element={<Donations />} />
            {/* Subscriptions route removed */}
            <Route path="fund/:creatorId" element={<MissionPage />} />
            <Route path="mission/:creatorId" element={<MissionPage />} />
          </Route>

          {/* Protected Creator Dashboard Routes */}
          <Route
            path="/creator"
            element={
              <ProtectedRoute userType="creator">
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<MyPage />} />
            <Route path="settings" element={<UserSettings />} />
            {/* Payment settings page removed; onboarding handled in-app via Stripe links */}
            {/* NESTED CONTENT ROUTES */}
            <Route path="content" element={<Outlet />}>
              <Route index element={<CreatorContentList />} />
              <Route path="add" element={<AddContentPage />} />
              <Route path=":contentId/edit" element={<EditContent />} />
              <Route path=":contentId" element={<ContentDetail />} />
            </Route>
            <Route path="membership" element={<Membership />} />
            <Route path="donations" element={<DonationsPage />} />
            <Route path="audience" element={<AudienceUsersPage />} />
          </Route>

          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          >
            <Route index element={<Users />} />
            <Route path="users" element={<Users />} />
            <Route path="compliance" element={<Compliance />} />
          </Route>

          {/* Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </AppErrorBoundary>
    </>
  );
}
